import { ReactNode } from 'react';

import {
  HexIcon,
  ProgressBar,
  Stack,
  Typography,
  TypographyProps,
  tokens,
} from '@cast/design-system';

type WizardStatusVariant =
  | 'castPilotAssistant'
  | 'castPilotAssistant2'
  | 'error'
  | 'success';

type WizardStatusLayoutProps = {
  variant: WizardStatusVariant;
  title: string;
  /** Defaults to 'A1'. */
  titleVariant?: TypographyProps['variant'];
  /** P12R secondary, tightly grouped below title (gap 4). */
  subtitle?: string;
  /** 0–100. When provided renders the progress bar. */
  progress?: number;
  /** P12R secondary, shown below the progress bar. */
  progressDescription?: string;
  /** P12R secondary, shown below the icon/title block when there is no progress bar. */
  description?: string;
  /** Arbitrary content rendered between the description and actions. */
  content?: ReactNode;
  actions?: ReactNode;
};

export const WizardStatusLayout = ({
  variant,
  title,
  titleVariant = 'A1',
  subtitle,
  progress,
  progressDescription,
  description,
  content,
  actions,
}: WizardStatusLayoutProps) => (
  <Stack
    alignItems="center"
    justifyContent="center"
    flex={1}
    gap={0}
    sx={{ py: 24, px: 32 }}
  >
    <Stack alignItems="center" gap={8}>
      <HexIcon size={variant === 'success' ? 48 : 56} variant={variant} />
      <Stack
        alignItems="center"
        gap={subtitle ? 4 : undefined}
        sx={{ textAlign: 'center' }}
      >
        <Typography variant={titleVariant}>{title}</Typography>
        {subtitle && (
          <Typography
            variant="P12R"
            color={tokens.theme.fg.structural.secondary}
          >
            {subtitle}
          </Typography>
        )}
      </Stack>
    </Stack>

    {progress !== undefined && (
      <Stack gap={8} mt={16} sx={{ width: '100%', maxWidth: 480 }}>
        <ProgressBar
          size="lg"
          value={progress}
          variant="determinate"
          sx={{
            '& .MuiLinearProgress-bar': {
              bgcolor: tokens.theme.bg.status.infoStrong,
            },
            bgcolor: tokens.theme.bg.status.neutralSubtle,
          }}
        />
        {progressDescription && (
          <Typography
            variant="P12R"
            color={tokens.theme.fg.structural.secondary}
            textAlign="center"
          >
            {progressDescription}
          </Typography>
        )}
      </Stack>
    )}
    {description && (
      <Typography variant="P12R" color={tokens.theme.fg.structural.secondary}>
        {description}
      </Typography>
    )}
    {content}
    {actions && (
      <Stack direction="row" gap={8} mt={24}>
        {actions}
      </Stack>
    )}
  </Stack>
);
